import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, Eye, EyeOff, Wifi, Globe, Clock, CheckCircle, Check, ArrowRight, UserCheck, Ban, Fingerprint } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const features = [
  {
    icon: EyeOff,
    title: "Hide your IP address",
    description: "Your IP address reveals your location and can be used to track you. SACVPN masks your real IP so websites and apps can't identify you."
  },
  {
    icon: Lock,
    title: "Strong encryption",
    description: "WireGuard's ChaCha20 encryption protects everything you do online. Your passwords, messages, and browsing stay private."
  },
  {
    icon: Wifi,
    title: "Public Wi-Fi security",
    description: "Coffee shops, airports, hotels: public Wi-Fi is a hacker's playground. SACVPN encrypts your connection on any network."
  },
  {
    icon: Ban,
    title: "Block trackers and ads",
    description: "Stop advertisers from following you across the web. Our DNS-level blocking removes trackers before they load."
  },
  {
    icon: Fingerprint,
    title: "Prevent fingerprinting",
    description: "Websites create unique profiles based on your device. A VPN is your first defense against browser fingerprinting."
  },
  {
    icon: Globe,
    title: "Access content anywhere",
    description: "Watch your favorite shows while traveling, reach region-locked content, and browse without geographic restrictions."
  }
];

const threats = [
  {
    title: "ISP monitoring",
    description: "Your internet provider sees every site you visit and can sell that data to advertisers.",
    icon: Eye
  },
  {
    title: "Public Wi-Fi hackers",
    description: "Attackers on public networks can intercept passwords, credit cards, and private messages.",
    icon: Wifi
  },
  {
    title: "Government surveillance",
    description: "Mass surveillance programs collect data on millions of ordinary people.",
    icon: Shield
  },
  {
    title: "Corporate tracking",
    description: "Tech companies build profiles on you based on every click, search, and purchase.",
    icon: UserCheck
  }
];

const useCases = [
  "Secure online banking",
  "Private browsing",
  "Streaming while traveling",
  "Protect family devices",
  "Safe public Wi-Fi use",
  "Block targeted ads",
  "Secure video calls",
  "Anonymous research"
];

const privacyCommitments = [
  { title: "No logs", desc: "We never record what you browse" },
  { title: "No selling data", desc: "Your data is yours alone" },
  { title: "Kill switch", desc: "Optional WireGuard app setting that blocks traffic if the tunnel drops" },
  { title: "US-based", desc: "A US-based company" }
];

export default function PersonalVPN() {
  return (
    <>
      <Helmet>
        <title>Personal VPN for Privacy | Protect Your Online Privacy | SACVPN</title>
        <meta
          name="description"
          content="Take back your online privacy. SACVPN hides your IP, encrypts your data with WireGuard, and blocks trackers. Secure your personal browsing on any device."
        />
        <meta
          name="keywords"
          content="personal VPN, privacy VPN, secure VPN, hide IP address, online privacy, VPN for privacy, anonymous browsing, private internet, encrypt connection, secure WiFi"
        />
        <link rel="canonical" href="https://www.sacvpn.com/personal" />
        <meta property="og:title" content="Personal VPN for Privacy | Protect Your Online Privacy | SACVPN" />
        <meta property="og:description" content="Take back your online privacy. SACVPN hides your IP, encrypts your data with WireGuard, and blocks trackers. Secure your personal browsing on any device." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Personal VPN Service",
            "name": "SACVPN Personal Privacy VPN",
            "description": "Personal VPN service for online privacy, secure browsing, and protection on any network.",
            "provider": {
              "@type": "Organization",
              "name": "SACVPN"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Worldwide"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Privacy-Conscious Individuals"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_78%_20%,rgba(14,165,233,0.14),transparent_60%)]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Personal privacy
              </motion.p>

              <motion.h1
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white leading-[1.05] tracking-tight"
              >
                Your privacy is{" "}
                <span className="text-brand-400">worth protecting</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="mt-6 text-lg text-gray-400 leading-relaxed max-w-xl"
              >
                Every website you visit, every search you make, every app you use is being tracked.
                SACVPN puts you back in control with WireGuard's modern encryption and a strict no-logs policy.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/download"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
                >
                  <Shield className="w-5 h-5" />
                  Protect my privacy
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
                >
                  <Clock className="w-5 h-5" />
                  14-day free trial
                </Link>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="relative">
              <div className="rounded-xl border border-white/[0.08] bg-dark-800/40 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Privacy protected</h3>
                    <p className="text-gray-500 text-sm">Your data, encrypted</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {["WireGuard (ChaCha20) encryption", "No browsing, DNS, or traffic logs", "IP address hidden", "Tracker blocking", "Kill switch available"].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-4 h-4 text-brand-400 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Privacy Commitments */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Our promise</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Privacy is the whole point
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {privacyCommitments.map((point, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                  <CheckCircle className="w-5 h-5 text-brand-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Threats Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">The risks</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Who's watching you online?
            </h2>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Without a VPN, your online activity is exposed to multiple parties.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {threats.map((threat, i) => {
              const Icon = threat.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="rounded-xl border border-white/[0.08] bg-dark-900 p-6 hover:border-brand-500/30 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{threat.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{threat.description}</p>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-12"
          >
            <p className="text-gray-400 mb-6">
              SACVPN helps guard against all of these with one connection.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
            >
              <Shield className="w-5 h-5" />
              Start protecting myself
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-dark-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-2xl mb-14"
          >
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">Protection</p>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">
              Everything you need to browse privately
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="bg-dark-900 p-8 hover:bg-dark-800 transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-dark-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">
                Everyday use
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-6"
              >
                Protect every part of your digital life
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-lg text-gray-400 mb-8 leading-relaxed"
              >
                From banking to browsing, streaming to shopping, SACVPN keeps your personal data private.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="grid grid-cols-2 gap-4"
              >
                {useCases.map((useCase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{useCase}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="rounded-xl border border-white/[0.08] bg-dark-900 p-8"
            >
              <h3 className="text-xl font-semibold text-white mb-6">Simple setup</h3>
              <div className="space-y-4">
                {[
                  "1. Download the SACVPN app",
                  "2. Create your account",
                  "3. Tap to connect",
                  "4. Browse privately"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-400">
                    <ArrowRight className="w-5 h-5 text-brand-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/[0.06]">
                <p className="text-gray-500 text-sm leading-relaxed">
                  Works on Windows, Mac, iOS, Android, and Linux. Connect unlimited devices with one account.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-900 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-4">
              Take back your privacy today
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-9">
              Protect your browsing with a private WireGuard network. Start your 14-day free trial, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/download"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
              >
                <Shield className="w-5 h-5" />
                Download free
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                View pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
